/**
 * Chat Service - Handles communication with the TheyChat Backend API
 *
 * Backend Routes:
 * - GET /avatar/list - Get all available avatars
 * - GET /avatar/accessible - Get avatars accessible to the user
 * - GET /avatar/{avatarId} - Get avatar details
 * - POST /avatar/{avatarId}/chat - Send a chat message
 * - GET /avatar/{avatarId}/{conversationId}/history - Get specific conversation history
 * - DELETE /avatar/{avatarId}/{conversationId}/history - Delete specific conversation
 * - PUT /avatar/{avatarId}/{conversationId}/feedback - Submit conversation feedback
 * - GET /avatar/{avatarId}/feedback - Get conversations with feedback
 */

import { logger } from '../utils/logger';

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'https://api.theychat.app').replace(
  /\/$/,
  '',
);

// Enable mock mode when API is not available (for development)
const USE_MOCK = !process.env.REACT_APP_API_URL;

// Types
export interface Avatar {
  id: string;
  name: string;
  sahabeName?: string;
  bio: string;
  image_url: string;
  assets?: {
    images?: Array<{
      url: string;
      s3Key?: string;
      filename?: string;
      isPrimary?: boolean;
    }>;
    videos?: Array<{
      url: string;
      s3Key?: string;
      filename?: string;
    }>;
  };
  required_tier: string;
  base_token_cost: number;
  tags: string[];
  accessible?: boolean;
  can_afford?: boolean;
  locked_reason?: string | null;
  personalityPrompt?: string;
  greetingMessage?: string;
}

export interface TokenUsageInfo {
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  contextWindowSize: number;
  maxOutputTokens: number;
  historyTruncated: boolean;
  historyMessagesRemoved: number;
}

export interface ChatMessage {
  id: string;
  conversationId: string;
  sender: 'user' | 'avatar';
  content: string;
  timestamp: string;
  metadata?: {
    responseTimeMs?: number;
    confidence?: number;
    tokensUsed?: number;
    tokenUsage?: TokenUsageInfo;
  };
}

// Backend returns snake_case, we'll transform it
interface BackendChatMessage {
  message_id: string;
  conversation_id: string;
  sender: 'user' | 'avatar';
  content: string;
  created_at: string;
  metadata?: {
    responseTimeMs?: number;
    confidence?: number;
    tokensUsed?: number;
    tokenUsage?: TokenUsageInfo;
  };
}

export interface Conversation {
  id: string;
  avatarId: string;
  avatarName?: string;
  title: string;
  lastMessageAt: string;
  lastMessagePreview?: string;
  messageCount: number;
  createdAt: string;
}

export interface ChatResponse {
  conversation_id: string;
  message_id: string;
  avatar_id: string;
  user_message: string;
  avatar_response: string;
  response_time_ms: number;
  timestamp: string;
  confidence_score?: number;
  tokens_used: number;
  tokens_remaining: number;
  tokenUsage?: TokenUsageInfo;
  is_new_conversation: boolean;
}

// Raw backend response format for conversation-specific history
interface BackendConversationHistoryResponse {
  avatar_id: string;
  conversation_id: string;
  title?: string;
  history: BackendChatMessage[];
  pagination: {
    limit: number;
    hasMore: boolean;
    nextCursor?: string | null;
  };
}

// Transformed frontend format
export interface ChatHistoryResponse {
  avatar_id: string;
  conversation_id?: string;
  history: ChatMessage[];
  conversations: Conversation[];
  pagination: {
    limit: number;
    hasMore: boolean;
  };
}

// Helper to get auth headers
// Note: API Gateway Cognito authorizer uses the Authorization header with ID token
const getAuthHeaders = (): Record<string, string> => {
  const idToken = localStorage.getItem('idToken');
  const language = localStorage.getItem('theychat-language') || 'en';

  return {
    'Content-Type': 'application/json',
    'Accept-Language': language,
    ...(idToken && { Authorization: `Bearer ${idToken}` }),
  };
};

// API Error handling
class ApiError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(
      errorData.error || errorData.message || 'An error occurred',
      response.status,
      errorData.code,
    );
  }
  return response.json();
};

/**
 * Get all available avatars
 */
export const getAvailableAvatars = async (
  query?: string,
): Promise<{ avatars: Avatar[]; total: number }> => {
  const url = new URL(`${API_BASE_URL}/avatar/list`);
  if (query) url.searchParams.append('q', query);

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

/**
 * Get avatars accessible to the current user based on their subscription
 */
export const getAccessibleAvatars = async (
  query?: string,
): Promise<{
  avatars: Avatar[];
  total: number;
  accessible_count: number;
  user_tier: string;
  token_balance: number;
}> => {
  const url = new URL(`${API_BASE_URL}/avatar/accessible`);
  if (query) url.searchParams.append('q', query);

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

/**
 * Get details for a specific avatar
 */
export const getAvatarDetails = async (avatarId: string): Promise<Avatar> => {
  const response = await fetch(`${API_BASE_URL}/avatar/${avatarId}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

/**
 * Send a chat message to an avatar
 * @throws {ApiError} When API call fails
 */
export const sendChatMessage = async (
  avatarId: string,
  message: string,
  conversationId?: string,
  context?: string,
): Promise<ChatResponse> => {
  logger.debug('ChatService', 'Sending chat message', { avatarId, conversationId });

  try {
    const response = await fetch(`${API_BASE_URL}/avatar/${avatarId}/chat`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        message,
        conversationId,
        context,
      }),
    });

    const result = await handleResponse<ChatResponse>(response);
    logger.info('ChatService', 'Chat message sent successfully', {
      conversationId: result.conversation_id,
      responseTimeMs: result.response_time_ms,
    });
    return result;
  } catch (error) {
    logger.error(
      'ChatService',
      'Failed to send chat message',
      error instanceof Error ? error : new Error(String(error)),
      { avatarId, conversationId },
    );
    throw error;
  }
};

/**
 * Mock chat response for development
 */

/**
 * Transform backend conversation history response to frontend format
 */
const transformConversationHistoryResponse = (
  backendResponse: BackendConversationHistoryResponse,
): ChatHistoryResponse => {
  return {
    avatar_id: backendResponse.avatar_id,
    conversation_id: backendResponse.conversation_id,
    history: (backendResponse.history || []).map((msg) => ({
      id: msg.message_id,
      conversationId: msg.conversation_id,
      sender: msg.sender,
      content: msg.content,
      timestamp: msg.created_at,
      metadata: msg.metadata,
    })),
    conversations: [], // Conversations list not included in conversation-specific endpoint
    pagination: {
      limit: backendResponse.pagination.limit,
      hasMore: backendResponse.pagination.hasMore || false,
    },
  };
};

/**
 * Get chat history with a specific avatar
 * @throws {ApiError} When API call fails
 * Get chat history for a specific conversation
 * @param avatarId - The avatar ID
 * @param conversationId - The conversation ID (required)
 * @param limit - Number of messages to return (default: 50)
 */
export const getChatHistory = async (
  avatarId: string,
  conversationId: string,
  limit: number = 50,
): Promise<ChatHistoryResponse> => {
  // Mock mode for development
  if (USE_MOCK) {
    return {
      avatar_id: avatarId,
      conversation_id: conversationId,
      history: [],
      conversations: [],
      pagination: { limit, hasMore: false },
    };
  }

  if (!conversationId) {
    throw new Error('conversationId is required');
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/avatar/${avatarId}/${conversationId}/history?limit=${limit}`,
      {
        method: 'GET',
        headers: getAuthHeaders(),
      },
    );
    const backendResponse: BackendConversationHistoryResponse = await handleResponse(response);
    return transformConversationHistoryResponse(backendResponse);
  } catch (error) {
    // Return empty history on error
    if (process.env.NODE_ENV === 'development') {
      return {
        avatar_id: avatarId,
        conversation_id: conversationId,
        history: [],
        conversations: [],
        pagination: { limit, hasMore: false },
      };
    }
    throw error;
  }
};

/**
 * Delete a specific conversation with an avatar
 * @param avatarId - The avatar ID
 * @param conversationId - The conversation ID to delete
 */
export const clearChatHistory = async (
  avatarId: string,
  conversationId: string,
): Promise<{ success: boolean; message?: string }> => {
  if (!conversationId) {
    throw new Error('conversationId is required');
  }

  const response = await fetch(`${API_BASE_URL}/avatar/${avatarId}/${conversationId}/history`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

// Backend response for user conversations
interface BackendUserConversationsResponse {
  conversations: {
    conversation_id: string;
    avatar_id: string;
    avatar_name: string;
    title: string;
    message_count: number;
    last_message_at: string;
    last_message_preview?: string;
    created_at: string;
    status: string;
  }[];
  total: number;
}

/**
 * Get all conversations for the current user (across all companions)
 * @throws {ApiError} When API call fails
 */
export const getUserConversations = async (
  query?: string,
): Promise<{
  conversations: Conversation[];
  total: number;
}> => {
  logger.debug('ChatService', 'Fetching user conversations');

  const url = new URL(`${API_BASE_URL}/user/conversations`);
  if (query) url.searchParams.append('q', query);

  try {
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: getAuthHeaders(),
    });
    const backendResponse: BackendUserConversationsResponse = await handleResponse(response);

    // Transform to frontend format
    const conversations: Conversation[] = backendResponse.conversations.map((conv) => ({
      id: conv.conversation_id,
      avatarId: conv.avatar_id,
      avatarName: conv.avatar_name,
      title: conv.title,
      lastMessageAt: conv.last_message_at,
      lastMessagePreview: conv.last_message_preview,
      messageCount: conv.message_count,
      createdAt: conv.created_at,
    }));

    logger.info('ChatService', 'User conversations fetched', {
      total: backendResponse.total,
    });
    return { conversations, total: backendResponse.total };
  } catch (error) {
    logger.error(
      'ChatService',
      'Failed to fetch user conversations',
      error instanceof Error ? error : new Error(String(error)),
    );
    throw error;
  }
};

/**
 * Delete a single conversation
 */
export const deleteConversation = async (conversationId: string): Promise<{ success: boolean }> => {
  const response = await fetch(`${API_BASE_URL}/user/conversations/${conversationId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

/**
 * Update a conversation (e.g., rename title)
 */
export const updateConversation = async (
  conversationId: string,
  data: { title: string },
): Promise<{ conversation: { conversation_id: string; title: string; updated_at: string } }> => {
  const response = await fetch(`${API_BASE_URL}/user/conversations/${conversationId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  return handleResponse(response);
};

/**
 * Create a new conversation
 */
export const createConversation = async (
  avatarId: string,
  title?: string,
): Promise<{
  conversation: { conversation_id: string; avatar_id: string; title: string; created_at: string };
}> => {
  const response = await fetch(`${API_BASE_URL}/user/conversations`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ avatar_id: avatarId, title }),
  });
  return handleResponse(response);
};

/** Submit user feedback (star rating + optional review) for a conversation */
export const submitConversationFeedback = async (
  avatarId: string,
  conversationId: string,
  rating: number,
  review?: string,
): Promise<{ message: string; conversation_id: string; rating: number; feedbackAt: string }> => {
  logger.debug('ChatService', 'Submitting conversation feedback', {
    avatarId,
    conversationId,
    rating,
  });

  try {
    const response = await fetch(`${API_BASE_URL}/avatar/${avatarId}/${conversationId}/feedback`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ rating, review: review || undefined }),
    });
    return handleResponse(response);
  } catch (error) {
    logger.error(
      'ChatService',
      'Failed to submit feedback',
      error instanceof Error ? error : new Error(String(error)),
      { avatarId, conversationId },
    );
    throw error;
  }
};

export { ApiError };
